import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import AddIcon from "@mui/icons-material/Add";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { PAGE_SIZE } from "../../services/common/const";
import {
  DeleteSpecialMedicalCondition,
  GetSpecialMedicalConditionList,
} from "../../services/SpecialMedicalConditionService";
import { PreferencesContext } from "../../PreferenceContext";
import { AlertType } from "../../services/models/AlertType";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

// SCHEMA
interface Data {
  specialMedicalConditionId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  specialMedicalConditionName: string;
  isDeleted: boolean;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  specialMedicalConditionId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialMedicalConditionId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  specialMedicalConditionColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  specialMedicalConditionColl: [],
  totalCount: 0,
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

const SpecialMedicalConditionMasterList = () => {
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let page = urlParams.get("page");
  const qParam = urlParams.get("q");
  let encodedQuery = qParam;
  let query = qParam || "";
  let curPage = 1;
  if (
    page != null &&
    page != "" &&
    page != undefined &&
    encodedQuery != null &&
    encodedQuery != undefined
  ) {
    curPage = +page;
    // Encoded Query
    encodedQuery = encodeURIComponent(encodedQuery);
    // Decode Query
    query = decodeURIComponent(encodedQuery);
  } else {
  }

  const [value, setValue] = useState(query || "");
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [specialMedicalConditionId, setSpecialMedicalConditionId] =
    useState<any>(null);
  const [param, setParam] = useState(parameters);
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);

  // HANDLE UPDATE
  const handleUpdate = (specialMedicalConditionId: any) => {
    let encryptSpecialMedicalConditionId: any = edService.encrypt(
      specialMedicalConditionId,
    );
    navigate(
      {
        pathname: "special-medical-condition-master-save",
        search: `?q=${encodeURIComponent(encryptSpecialMedicalConditionId)}`,
      },
      {
        state: {
          title: " Edit",
          button: "Update",
          specialMedicalConditionId: specialMedicalConditionId,
        },
      },
    );
  };

  // ON SERCH
  function searchValue(val: any) {
    val = val.trim();
    // ENCODE THE SEACH VALUE
    navigate(`?page=1&q=${encodeURIComponent(val)}`);
  }

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "95%",
            }}
            align="left">
            Medical Condition Name
          </TableCell>

          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "5%",
            }}
            align="left"></TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.specialMedicalConditionColl?.map(
        (n: any) => n?.specialMedicalConditionId || 0,
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    specialMedicalConditionId: any,
  ) => {
    setAnchorEl3(event.currentTarget);
    setSpecialMedicalConditionId(specialMedicalConditionId);
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (curPage !== value) {
      navigate(`?page=${value}&q=${encodeURIComponent(query)}`);
    }
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value) => {
    setOpenDeleteDilog(value);
  };

  // GET LIST OF CONSUMER
  const getSpecialMedicalConditionList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", curPage?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", query || "");
      const result: any = await GetSpecialMedicalConditionList(params);
      if (result?.data?.data?.specialMedicalConditionColl) {
        const resultData: ResultData = result?.data?.data;
        let specialMedicalConditionColl =
          resultData?.specialMedicalConditionColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (curPage - 1) * param.pageSize + 1;
        let endIndex =
          (curPage - 1) * param.pageSize +
          (specialMedicalConditionColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  // HANDLE DELETE
  async function deleteRow(specialMedicalConditionIds: any) {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        specialMedicalConditionIds: specialMedicalConditionIds?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        orgId: userContext?.organizationId?.toString(),
        branchId: userContext?.branchId?.toString(),
        isDeleted: true,
        dataManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteSpecialMedicalCondition(DeleteData);
      if (result) {
        await getSpecialMedicalConditionList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    getSpecialMedicalConditionList();
  }, [param, curPage, query]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box
                sx={{ display: "flex", height: "100%", alignItems: "center" }}>
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleUpdate(0);
                  }}
                  size="medium"
                  startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid item xs={8} sm={8} md={8} lg={10} sx={{ gap: 2 }}>
              <SkeletonWrapper
                isLoading={isLoading}
                className="skeleton-search-box">
                <TextField
                  id="searchBox"
                  placeholder="Search..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      searchValue(value);
                    }
                  }}
                  type="text"
                  variant="outlined"
                  size="small"
                  onChange={(e) => setValue(e.target.value)}
                  value={value}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={() => {
                            searchValue(value);
                          }}>
                          {" "}
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: value && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => {
                          searchValue("");
                          setValue("");
                        }}>
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
              </SkeletonWrapper>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} mt={2}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={
                      resultData?.specialMedicalConditionColl?.length || 0
                    }
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    //onRequestSort={handleRequestSort}
                    rowCount={resultData?.specialMedicalConditionColl?.length}
                  />
                  <TableBody>
                    {showSkeleton ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={showSkeleton}
                                  className="skeleton-input">
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.specialMedicalConditionColl,
                          getComparator(order, orderBy),
                        ).map((item: any, index: any) => {
                          return (
                            <TableRow
                              key={item?.specialMedicalConditionId?.toString()}>
                              <TableCell>
                                {!item.isLocked ? (
                                  <Link
                                    className="text-link"
                                    to={{
                                      pathname:
                                        "special-medical-condition-master-save",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.specialMedicalConditionId || 0,
                                        ) || 0,
                                      )}`,
                                    }}>
                                    {item.medicalConditionName
                                      ? item.medicalConditionName
                                      : ""}
                                  </Link>
                                ) : (
                                  <Typography sx={{ fontWeight: 500 }}>
                                    {" "}
                                    {item.medicalConditionName
                                      ? item.medicalConditionName
                                      : ""}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {!item.isLocked ? (
                                  <Tooltip title="More" placement="top">
                                    <IconButton
                                      onClick={(e: any) => {
                                        handleOpenMore(
                                          e,
                                          item?.specialMedicalConditionId || 0,
                                        );
                                      }}>
                                      <MoreVertRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <LockRoundedIcon
                                    sx={{ paddingY: "2px", color: "#74788d" }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {resultData?.specialMedicalConditionColl?.length <=
                          0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.specialMedicalConditionColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-pagination-label">
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-pagination-stack">
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={curPage} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleUpdate(specialMedicalConditionId);
              }}>
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              className="delete-container"
              onClick={() => {
                handleDeleteDilog(true);
              }}>
              Delete
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this Special Medical Condition?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(specialMedicalConditionId);
            }}
            className="button-delete">
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SpecialMedicalConditionMasterList;
